import { Button, Textarea } from "@scribenote/brian-design-system";
import { useState } from "react";

import {
  candidatesOf,
  type Question,
  type StaffResponse,
} from "./questionsClient";

type AnswerInput = Omit<
  StaffResponse,
  | "operation_id"
  | "expected_revision"
  | "rejected_candidate_ranks"
  | "unresolved_reasons"
> &
  Partial<
    Pick<StaffResponse, "rejected_candidate_ranks" | "unresolved_reasons">
  >;

export function QuestionAnswer({
  question,
  disabled,
  rankedRanks,
  onSubmit,
}: {
  question: Question;
  disabled: boolean;
  rankedRanks: number[];
  onSubmit: (body: AnswerInput) => void;
}) {
  const candidates = candidatesOf(question.candidates).sort((a, b) => {
    const order = (rank: number) => {
      const index = rankedRanks.indexOf(rank);
      return index === -1 ? rankedRanks.length : index;
    };
    return order(a.rank) - order(b.rank) || a.rank - b.rank;
  });
  const [choice, setChoice] = useState("");
  const [statement, setStatement] = useState("");
  const [unresolved, setUnresolved] = useState("");
  const candidate = candidates.find((c) => String(c.rank) === choice);
  const submit = () => {
    if (candidate)
      onSubmit({
        action: "answer",
        candidate_rank: candidate.rank,
        statement: statement.trim() || `I confirm: ${candidate.label}`,
      });
    else if (choice === "none")
      onSubmit({
        action: "none",
        statement: statement.trim() || "None of these is the item.",
        rejected_candidate_ranks: candidates.map((c) => c.rank),
      });
    else if (choice === "unknown")
      onSubmit({
        action: "reject",
        statement: statement.trim() || "Not enough to tell.",
        rejected_candidate_ranks: candidates.map((c) => c.rank),
        unresolved_reasons: ["The correct answer is still unknown."],
      });
    else if (choice === "partial")
      onSubmit({
        action: "partial",
        statement: statement.trim(),
        answer: statement.trim(),
        unresolved_reasons: [unresolved.trim()],
      });
    else if (choice === "moot")
      onSubmit({ action: "moot", statement: statement.trim() });
    else
      onSubmit({
        action: "answer",
        statement: statement.trim(),
        answer: statement.trim(),
      });
  };
  const valid = Boolean(
    choice &&
    (candidate ||
      choice === "none" ||
      choice === "unknown" ||
      statement.trim()) &&
    (choice !== "partial" || unresolved.trim()),
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit();
      }}
      className="mt-4 space-y-3"
    >
      <fieldset disabled={disabled} className="space-y-2">
        <legend className="mb-2 text-sm font-semibold">Your answer</legend>
        {[
          ...candidates.map((c) => ({ value: String(c.rank), label: c.label })),
          { value: "none", label: "None of these" },
          { value: "unknown", label: "Not enough to tell" },
          { value: "other", label: "A different answer" },
          { value: "partial", label: "I can answer part of this" },
          { value: "moot", label: "An answer is no longer needed" },
        ].map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-2 rounded border border-rule px-3 py-2 text-sm has-[:checked]:border-accent has-[:checked]:bg-wall"
          >
            <input
              type="radio"
              name="question-answer"
              value={option.value}
              checked={choice === option.value}
              onChange={() => setChoice(option.value)}
              className="mt-1 accent-accent"
            />
            {option.label}
          </label>
        ))}
        {choice && (
          <label className="block text-sm">
            {candidate || choice === "none" || choice === "unknown"
              ? "Additional context (optional)"
              : choice === "moot"
                ? "Why is an answer no longer needed?"
                : "What do you know?"}
            <Textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={2}
              maxLength={8000}
              className="mt-1"
            />
          </label>
        )}
        {choice === "partial" && (
          <label className="block text-sm">
            What still needs an answer?
            <Textarea
              value={unresolved}
              onChange={(e) => setUnresolved(e.target.value)}
              rows={2}
              maxLength={8000}
              className="mt-1"
            />
          </label>
        )}
      </fieldset>
      <p className="text-xs text-annotation">Saved with your staff identity.</p>
      <Button type="submit" size="sm" disabled={disabled || !valid}>
        {disabled
          ? "Saving…"
          : choice === "none"
            ? "None of these"
            : choice === "unknown"
              ? "Keep waiting"
              : choice === "partial"
                ? "Save partial answer"
                : choice === "moot"
                  ? "Mark no longer needed"
                  : "Confirm answer"}
      </Button>
    </form>
  );
}
